import React from 'react';
import classNames from 'classnames';
import {Classes, Icon, MenuItem, Position, Tooltip} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {ItemRendererProps} from '@blueprintjs/select';
import {DropDown, SelectItem} from '@zaiusinc/hera';
import {toJS} from 'mobx';
import {observer} from 'mobx-react-lite';
import {useHistory} from 'react-router-dom';
import styles from './CmsInstanceSelector.module.sass';
import {AppDetailStore} from '../../../stores/AppDetailStore';
import {addToQuery} from '../../../lib/history';

interface Props {
  detailStore: AppDetailStore;
}

interface InstanceItem extends SelectItem {
  value: string;
  installed: boolean;
  installedVersion?: string;
}

export const CmsInstanceSelector = observer(({detailStore}: Props) => {
  const history = useHistory();

  // The open tab lives in the URL, and Blueprint renders a disabled selected tab's panel anyway,
  // so switching to an uninstalled instance would leave Settings on screen.
  const selectInstance = (scope: string) => {
    if (scope !== detailStore.selectedCmsScope) {
      addToQuery(history, {tab: 'overview'});
    }
    detailStore.selectCmsScope(scope);
  };

  // toJS because MobX 4 observable arrays do not implement flatMap
  const items: InstanceItem[] = toJS(detailStore.cmsProjects).flatMap((project) =>
    project.instances.map((instance) => {
      const installation = detailStore.installationForScope(instance.id);
      const managedByOther = instance.managed_by_different_account;
      return {
        text: instance.name,
        value: instance.id,
        installed: !!installation,
        installedVersion: installation?.version,
        disabled: managedByOther,
        disabledTooltip: managedByOther
          ? 'This CMS instance is managed by a different OCP account'
          : undefined
      };
    })
  );

  // Hera renders a single line of text per item; each instance needs its version underneath.
  const itemRenderer = (item: InstanceItem, {handleClick, modifiers}: ItemRendererProps) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }

    const row = (
      <MenuItem
        key={item.value}
        multiline={true}
        // `selected` is only honoured for the listoption role
        roleStructure="listoption"
        selected={detailStore.selectedCmsScope === item.value}
        disabled={item.disabled}
        active={modifiers.active}
        onClick={handleClick}
        text={
          <div className={styles.instanceInfo}>
            <div className={styles.instanceName}>{item.text}</div>
            {item.installedVersion && (
              <div className={styles.instanceVersion}>v{item.installedVersion}</div>
            )}
          </div>
        }
        labelElement={item.installed && !item.disabled ? (
          <Icon icon={IconNames.TICK_CIRCLE} className={styles.instanceCheck} />
        ) : undefined}
      />
    );

    // Overriding itemRenderer bypasses Hera's selectItemRenderer, which normally renders this
    return item.disabledTooltip ? (
      <Tooltip
        key={item.value}
        content={item.disabledTooltip}
        hoverOpenDelay={750}
        position={Position.TOP}
      >
        {row}
      </Tooltip>
    ) : row;
  };

  return (
    <div className={styles.selector}>
      <DropDown
        items={items}
        value={detailStore.selectedCmsScope}
        loading={detailStore.scopeLoading}
        initialContent="Select Instance"
        itemRenderer={itemRenderer}
        onItemSelect={(item) => selectInstance(item.value)}
        // `minimal` is load-bearing: Hera's rebrand theme forces a white background on
        // `.bp5-button:not(...):not(.bp5-minimal):not(.bp5-outlined)` with !important, which
        // nothing here can outrank. className replaces Hera's rather than merging, which also
        // drops its width caps — so Classes.FILL has to be re-added by hand.
        buttonProps={{className: classNames(Classes.FILL, styles.trigger), minimal: true}}
        // The popover is portaled, so .selector-scoped CSS cannot reach it. Passing
        // popoverProps replaces Hera's defaults, hence carrying h-DropDown-popover over.
        popoverProps={{
          matchTargetWidth: true,
          popoverClassName: classNames('h-DropDown-popover', styles.menu)
        }}
      />
    </div>
  );
});
